import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { HardDrive, Loader2, Copy, Save, Trash2, Link2, Eye, EyeOff, Activity, RefreshCw, CheckCircle2, XCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const copy = (v, label) => navigator.clipboard?.writeText(v).then(() => toast.success(`${label} copied`)).catch(() => toast.error('Copy failed'));

/** Admin → Settings → Integrations (Super Admin): Google Drive OAuth client stored in the database, overriding environment variables. */
export function GoogleDriveSettingsCard() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ client_id: '', client_secret: '', redirect_uri: '', frontend_url: '' });
  const [showSecret, setShowSecret] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/admin/integrations/google-drive`, { withCredentials: true });
      setData(r.data);
      const s = r.data.saved;
      setForm({ client_id: s?.client_id || r.data.environment?.client_id || '', client_secret: '',
        redirect_uri: s?.redirect_uri || r.data.suggested.redirect_uri, frontend_url: s?.frontend_url || r.data.suggested.frontend_url });
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not load Google Drive settings'); setData({}); }
  };
  useEffect(() => { load(); }, []);
  const save = async () => {
    setBusy(true);
    try {
      await axios.put(`${API_URL}/api/admin/integrations/google-drive`, form, { withCredentials: true });
      toast.success('Google Drive client saved — it now overrides the environment variables');
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save'); } finally { setBusy(false); }
  };
  const clear = async () => {
    if (!window.confirm('Remove the saved Google Drive client? The app falls back to the environment variables (if any).')) return;
    setBusy(true);
    try { await axios.delete(`${API_URL}/api/admin/integrations/google-drive`, { withCredentials: true }); toast.success('Saved client removed'); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not remove'); } finally { setBusy(false); }
  };
  const connect = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/rfp/drive/connect`, { withCredentials: true });
      window.location.href = r.data.authorization_url;
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not start the Google sign-in'); }
  };
  if (!data) return <Loader2 className="w-5 h-5 animate-spin text-[#4ABFCF] mx-auto my-6" />;
  const src = data.source;
  const row = (label, value, testId) => (
    <div className="flex items-center gap-2 text-xs">
      <span className="w-44 shrink-0 text-slate-500">{label}</span>
      <code className="flex-1 min-w-0 truncate bg-slate-50 border border-slate-200 rounded px-2 py-1 text-[11px] text-[#0F2040]" data-testid={testId}>{value}</code>
      <button type="button" onClick={() => copy(value, label)} className="text-slate-400 hover:text-[#0F2040]" title="Copy" data-testid={`${testId}-copy`}><Copy className="w-3.5 h-3.5" /></button>
    </div>
  );
  return (
    <Card data-testid="admin-google-drive-settings">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-center gap-2 flex-wrap">
          <HardDrive className="w-4 h-4 text-[#4ABFCF]" />
          <h3 className="font-semibold text-sm text-[#0F2040]">Google Drive (RFP attachment library)</h3>
          <Badge variant="outline" className={`text-[9px] ${src === 'none' ? 'bg-rose-50 text-rose-700 border-rose-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}`} data-testid="gdrive-source-badge">
            {src === 'database' ? 'Configured here' : src === 'environment' ? 'From environment variables' : 'Not configured'}
          </Badge>
          {data.connected_accounts > 0 && <Badge variant="outline" className="text-[9px]" data-testid="gdrive-connected-count">{data.connected_accounts} account{data.connected_accounts === 1 ? '' : 's'} connected</Badge>}
          {data.folder?.name && <Badge variant="outline" className="text-[9px]" data-testid="gdrive-folder">folder: {data.folder.name}</Badge>}
        </div>
        <p className="text-xs text-slate-500">
          Paste the OAuth 2.0 <b>Web application</b> client from Google Cloud Console. Values saved here are encrypted and take precedence over
          <code className="mx-1">GOOGLE_CLIENT_ID / GOOGLE_CLIENT_SECRET / GOOGLE_DRIVE_REDIRECT_URI</code>, so production can be configured without redeploying.
        </p>
        <div className="space-y-1.5 border border-slate-200 rounded-lg p-3 bg-slate-50/50">
          <p className="text-[11px] font-semibold text-[#0F2040]">Add these to the Google client for this host</p>
          {row('Authorized redirect URI', data.suggested.redirect_uri, 'gdrive-suggested-redirect')}
          {row('Authorized JavaScript origin', data.suggested.javascript_origin, 'gdrive-suggested-origin')}
          <p className="text-[10px] text-slate-500">Google matches the redirect URI exactly — use the domain users actually open (e.g. the custom domain), not a URL that redirects to it. For a Workspace-only user base set the consent screen to <b>Internal</b> so no verification is needed and refresh tokens don't expire after 7 days.</p>
        </div>
        <div className="grid md:grid-cols-2 gap-3 text-xs">
          <label className="space-y-1">
            <span className="text-slate-500">Client ID</span>
            <Input value={form.client_id} onChange={e => setForm(f => ({ ...f, client_id: e.target.value }))} placeholder="1234567890-abc.apps.googleusercontent.com" className="h-8 text-xs" data-testid="gdrive-client-id" />
          </label>
          <label className="space-y-1">
            <span className="text-slate-500">Client secret {data.saved?.client_secret_masked ? <span className="text-slate-400">(saved {data.saved.client_secret_masked} — leave blank to keep)</span> : null}</span>
            <div className="flex items-center gap-1">
              <Input type={showSecret ? 'text' : 'password'} value={form.client_secret} onChange={e => setForm(f => ({ ...f, client_secret: e.target.value }))} placeholder={data.saved ? '••••••••' : 'GOCSPX-…'} className="h-8 text-xs" data-testid="gdrive-client-secret" autoComplete="off" />
              <button type="button" onClick={() => setShowSecret(s => !s)} className="text-slate-400 hover:text-[#0F2040]" data-testid="gdrive-secret-toggle">{showSecret ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}</button>
            </div>
          </label>
          <label className="space-y-1">
            <span className="text-slate-500">Redirect URI</span>
            <Input value={form.redirect_uri} onChange={e => setForm(f => ({ ...f, redirect_uri: e.target.value }))} className="h-8 text-xs" data-testid="gdrive-redirect-uri" />
          </label>
          <label className="space-y-1">
            <span className="text-slate-500">Public app URL (where users return after Google sign-in)</span>
            <Input value={form.frontend_url} onChange={e => setForm(f => ({ ...f, frontend_url: e.target.value }))} placeholder="https://blueprint.seertech.ai" className="h-8 text-xs" data-testid="gdrive-frontend-url" />
          </label>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button size="sm" onClick={save} disabled={busy || !form.client_id || !form.redirect_uri} className="h-8 text-xs bg-[#0F2040] hover:bg-[#1A3055] text-white" data-testid="gdrive-save-btn">
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}Save client
          </Button>
          {src !== 'none' && (
            <Button size="sm" variant="outline" onClick={connect} className="h-8 text-xs" data-testid="gdrive-connect-btn">
              <Link2 className="w-3.5 h-3.5 mr-1" />Connect my Google Drive (test)
            </Button>
          )}
          {data.saved && (
            <Button size="sm" variant="ghost" onClick={clear} disabled={busy} className="h-8 text-xs text-rose-600 hover:text-rose-700 hover:bg-rose-50 ml-auto" data-testid="gdrive-clear-btn">
              <Trash2 className="w-3.5 h-3.5 mr-1" />Remove saved client
            </Button>
          )}
        </div>
        {data.saved?.updated_at && <p className="text-[10px] text-slate-400" data-testid="gdrive-saved-meta">Saved {new Date(data.saved.updated_at).toLocaleString()} by {data.saved.updated_by}</p>}
        {data.environment?.present && src === 'database' && <p className="text-[10px] text-slate-400">Environment variables are also set ({data.environment.redirect_uri}) but are overridden by the saved client.</p>}
        <DriveHealthPanel configured={src !== 'none'} />
      </CardContent>
    </Card>
  );
}

const fmt = (v) => (v ? new Date(v).toLocaleString() : '—');

/** Live status: does each connected token still work, is the folder reachable, when did the nightly sync last run. */
function DriveHealthPanel({ configured }) {
  const [h, setH] = useState(null);
  const [busy, setBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const check = async () => {
    setBusy(true);
    try { const r = await axios.get(`${API_URL}/api/admin/integrations/google-drive/health`, { withCredentials: true }); setH(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Health check failed'); }
    finally { setBusy(false); }
  };
  useEffect(() => { if (configured) check(); }, [configured]);
  const syncNow = async () => {
    setSyncing(true);
    try {
      const r = await axios.post(`${API_URL}/api/rfp/drive/sync`, {}, { withCredentials: true });
      toast.success(`Sync started${r.data?.checked !== undefined ? ` — ${r.data.checked} checked, ${r.data.updated} updated` : ''}`);
      setTimeout(check, 1500);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not start sync'); } finally { setSyncing(false); }
  };
  if (!configured) return null;
  const tone = !h ? 'bg-slate-50 text-slate-500 border-slate-200' : h.healthy ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : h.accounts.length ? 'bg-rose-50 text-rose-700 border-rose-200' : 'bg-amber-50 text-amber-700 border-amber-200';
  const label = !h ? 'checking…' : h.healthy ? 'Healthy' : h.accounts.length ? 'Attention needed' : 'No account connected';
  return (
    <div className="border border-slate-200 rounded-lg p-3 space-y-2" data-testid="gdrive-health">
      <div className="flex items-center gap-2 flex-wrap">
        <Activity className="w-3.5 h-3.5 text-[#4ABFCF]" />
        <span className="text-xs font-semibold text-[#0F2040]">Drive health</span>
        <Badge variant="outline" className={`text-[9px] ${tone}`} data-testid="gdrive-health-badge">{label}</Badge>
        {h?.checked_at && <span className="text-[10px] text-slate-400">checked {fmt(h.checked_at)}</span>}
        <span className="ml-auto flex items-center gap-1.5">
          <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={check} disabled={busy} data-testid="gdrive-health-refresh">{busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <><RefreshCw className="w-3 h-3 mr-1" />Re-check</>}</Button>
          <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={syncNow} disabled={syncing || !h?.accounts?.length} data-testid="gdrive-health-sync-now">{syncing ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Sync now'}</Button>
        </span>
      </div>
      {h && (
        <>
          <div className="text-xs divide-y divide-slate-100 border border-slate-100 rounded-md" data-testid="gdrive-health-accounts">
            {h.accounts.length === 0 && <p className="p-2 text-slate-500">No Google account has connected Drive yet — use "Connect my Google Drive (test)" above.</p>}
            {h.accounts.map(a => (
              <div key={a.user_id} className="p-2 flex items-center gap-2 flex-wrap" data-testid={`gdrive-health-account-${a.user_id}`}>
                {a.ok ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> : <XCircle className="w-3.5 h-3.5 text-rose-600" />}
                <span className="font-medium text-[#0F2040]">{a.user || a.user_id}</span>
                {a.drive_account && a.drive_account !== a.user && <span className="text-[10px] text-slate-500">Drive: {a.drive_account}</span>}
                <span className="text-[10px] text-slate-400">connected {fmt(a.connected_at)}</span>
                {a.folder_ok === true && <Badge variant="outline" className="text-[9px] bg-emerald-50 text-emerald-700 border-emerald-200">folder OK{a.folder_name ? ` · ${a.folder_name}` : ''}</Badge>}
                {a.folder_ok === false && <Badge variant="outline" className="text-[9px] bg-rose-50 text-rose-700 border-rose-200">folder not accessible</Badge>}
                {a.error && <span className="text-[10px] text-rose-600 ml-auto" data-testid={`gdrive-health-error-${a.user_id}`}>{a.error}</span>}
              </div>
            ))}
          </div>
          <div className="grid sm:grid-cols-3 gap-2 text-[11px]">
            <div className="border border-slate-100 rounded-md p-2" data-testid="gdrive-health-folder">
              <p className="text-[10px] text-slate-500">Library folder</p>
              <p className="font-medium text-[#0F2040] truncate">{h.folder?.name || 'not set'}</p>
              <p className="text-[10px] text-slate-400">{h.drive_items} Drive document{h.drive_items === 1 ? '' : 's'} in the library</p>
            </div>
            <div className="border border-slate-100 rounded-md p-2" data-testid="gdrive-health-last-sync">
              <p className="text-[10px] text-slate-500">Last sync</p>
              <p className={`font-medium ${h.sync_stale ? 'text-amber-700' : 'text-[#0F2040]'}`}>{h.last_sync ? `${fmt(h.last_sync.at)} (${h.last_sync.trigger})` : 'never'}</p>
              {h.last_sync && <p className="text-[10px] text-slate-400">{h.last_sync.checked} checked · {h.last_sync.updated} updated · {(h.last_sync.errors || []).length} error{(h.last_sync.errors || []).length === 1 ? '' : 's'}{h.sync_stale ? ' · overdue' : ''}</p>}
            </div>
            <div className="border border-slate-100 rounded-md p-2" data-testid="gdrive-health-next-sync">
              <p className="text-[10px] text-slate-500">Next nightly sync</p>
              <p className="font-medium text-[#0F2040]">{fmt(h.next_sync_utc)}</p>
              <p className="text-[10px] text-slate-400">02:00 Australia/Sydney · needs WEBHOOK_CRON_SECRET in production</p>
            </div>
          </div>
          {h.last_sync?.errors?.length > 0 && (
            <ul className="text-[10px] text-rose-600 list-disc pl-4" data-testid="gdrive-health-sync-errors">{h.last_sync.errors.slice(0, 5).map((e, i) => <li key={i}>{typeof e === 'string' ? e : JSON.stringify(e)}</li>)}</ul>
          )}
        </>
      )}
    </div>
  );
}
